import { useMemo, useRef, useState, useEffect } from 'react';
import { add as addTime, compareAsc, min as minTime, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import { makeStyles, DefaultTheme } from '@material-ui/styles';

import { DaySelect, MainButton, CheckAllButton, AddFill } from '@bizzabo/design-system';
import { useActivities, Activity } from '@bizzabo/entities';

import TimeButton, { TimeButtonType } from '../TimeButton/TimeButton';
import TimePickerButtonWrapper from './TimePickerButtonWrapper';
import { calculateOverlapTimes } from '../../SchedulingWizard/helpers/overlapSessionHelper';
import { useMeetings } from '../../MeetingsTool/MeetingsToolContext';
import useTypeTracking from '../../../../analytics';
import { TimeSlot, TimesGroup } from '../../types';
import { groupByZonedDayToArray } from '../../helpers';
import OverlapSessionsModal, { OverlapSessionsModalData } from '../OverlapSessionsModal';

export interface TimePickerProps {
    times: TimeSlot[];
    timeZone: string;
    selectedDates?: Date[];
    duration?: number;
    variant?: TimeButtonType.Toggle | TimeButtonType.ToggleFill;
    showCheckAll?: boolean;
    isTooltipShow?: boolean;
    foldAfter?: number;
    onChange?: (dates: Date[]) => void; // handler for clicking on a time slot
    onSelect?: (dates: Date[]) => void; // handler for check all action
    onDeselect?: (dates: Date[]) => void; // handler for uncheck all action
    onLoadMore?: (dates: Date[]) => void;
}

function sortedTimesGroups(timesGroups: TimesGroup[]): TimesGroup[] {
    return timesGroups.map(({ day, times }) => ({
        day,
        times: [...times].sort(({ time: time1 }, { time: time2 }) => compareAsc(time1, time2)),
    }));
}

const DEFAULT_SELECTED_DATES = [];
const DEFAULT_CALLBACK = () => {};
const TimePicker: React.FC<TimePickerProps> = ({
    times,
    timeZone,
    selectedDates = DEFAULT_SELECTED_DATES,
    duration,
    variant = TimeButtonType.Toggle,
    showCheckAll = false,
    foldAfter = undefined,
    onChange = DEFAULT_CALLBACK,
    onSelect = DEFAULT_CALLBACK,
    onDeselect = DEFAULT_CALLBACK,
    onLoadMore = DEFAULT_CALLBACK,
}) => {
    const { eventUniqueName } = useMeetings();
    const { trackClickEvent } = useTypeTracking();

    const firstDate = minTime(times.map(t => t.time));
    const [pagination, setPagination] = useState(startOfDay(firstDate).getTime());
    const { data: activities = [] } = useActivities(eventUniqueName);

    const [overlapSessionsModalData, setOverlapSessionsModalData] =
        useState<OverlapSessionsModalData>();
    const [groupedTimes, setGroupedTimes] = useState<TimesGroup[]>([]);
    // mapping from day (epoch time) to the amount of times to display
    const [pageFolds, setPageFolds] = useState<Record<number, number>>({});
    const [scrollToBottom, setScrollToBottom] = useState(false);
    const buttonWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollToBottom) {
            // Scroll to the bottom of the container
            buttonWrapperRef.current.scrollTo({
                behavior: 'smooth',
                top: buttonWrapperRef.current.scrollHeight,
            });
            setScrollToBottom(false);
        }
    }, [scrollToBottom]);

    useEffect(() => {
        const groupedTimeSlot = groupByZonedDayToArray<TimeSlot>(times, timeZone, t => t.time);
        const timesGroup: TimesGroup[] = groupedTimeSlot.map(({ day, values }) => ({
            day,
            times: values,
        }));
        setGroupedTimes(sortedTimesGroups(timesGroup));

        setPageFolds(
            timesGroup.reduce(
                (acc, { day }) => ({ ...acc, [day]: acc[day] || foldAfter }),
                pageFolds
            )
        );
    }, [times, timeZone, foldAfter]);

    const pageData = useMemo(
        () =>
            groupedTimes.map(({ day: value }) => ({
                value,
                label: format(new Date(value), 'EEE, MMM. dd'),
            })),
        [groupedTimes, timeZone]
    );

    const thisPageTimes = useMemo(() => {
        return groupedTimes.find(({ day }) => day === pagination);
    }, [groupedTimes, pagination]);

    // apply any folding to this page and annotate with overlap
    const timesToDisplay = useMemo(() => {
        const timesWithOverlap = calculateOverlapTimes(thisPageTimes?.times, duration, activities);
        if (typeof foldAfter === 'number') {
            return timesWithOverlap?.slice(0, pageFolds[pagination]);
        }
        return timesWithOverlap;
    }, [thisPageTimes, duration, activities, foldAfter, pagination, pageFolds]);

    const daysFromDefault = useMemo(
        () => pageData.findIndex(page => page.value === pagination),
        [pageData, pagination]
    ); // data for tracking

    const onDayPickerChange = (value: number) => {
        setPagination(value as number);
        trackClickEvent({
            subCategory: 'SWITCH_TIMESLOT_DATE',
            description: {
                days_from_default: pageData.findIndex(page => page.value === value),
            },
        });
    };

    const onLoadMoreClick = () => {
        const oldFoldPoint = pageFolds[pagination];
        setPageFolds({ ...pageFolds, [pagination]: oldFoldPoint + foldAfter });
        setScrollToBottom(true);

        if (onLoadMore) {
            const newTimesShown = thisPageTimes?.times
                .slice(oldFoldPoint, oldFoldPoint + foldAfter)
                .map(({ time }) => time);
            onLoadMore(newTimesShown);
        }

        trackClickEvent({
            subCategory: 'LOAD_MORE_TIMES',
            description: {
                days_from_default: daysFromDefault,
            },
        });
    };

    const checkAllChecked = useMemo(
        () =>
            showCheckAll &&
            timesToDisplay.every(({ time }) =>
                selectedDates.some(selectedDate => selectedDate.getTime() === time.getTime())
            ),
        [showCheckAll, timesToDisplay, selectedDates]
    );
    const onCheckAllClicked = () => {
        const displayedTimes = timesToDisplay.map(({ time }) => time);

        if (checkAllChecked) {
            onDeselect(displayedTimes);
            trackClickEvent({
                subCategory: 'MEETING_INVITATION_UNCHECK_ALL',
                description: {
                    days_from_default: daysFromDefault,
                },
            });
        } else {
            onSelect(displayedTimes);
            trackClickEvent({
                subCategory: 'MEETING_INVITATION_CHECK_ALL',
                description: {
                    days_from_default: daysFromDefault,
                },
            });
        }
    };

    const spEventsHandle = ({ time, text }: TimeSlot) => {
        const isTimeSelected = selectedDates.some(
            selectedDate => selectedDate.getTime() === time.getTime()
        );
        const subCategory = isTimeSelected
            ? 'MEETING_TIMESLOT_DESELECTED'
            : 'MEETING_TIMESLOT_SELECTED';
        const timeslot_order = timesToDisplay.findIndex(
            timeToDisplay => timeToDisplay.time === time
        );

        trackClickEvent({
            subCategory,
            description: {
                timeslot_order,
                days_from_default: daysFromDefault,
                is_overlap: !!text,
                overlap_type: text ? 'session' : null,
            },
        });
    };

    const onTimeSlotClick = (time: Date, text: string) => {
        onChange([time]);
        spEventsHandle({ time, text });
    };

    const openOverlapSessionsModal = (
        e: React.MouseEvent<HTMLElement>,
        overlapSessions: Activity[]
    ) => {
        e.stopPropagation();
        setOverlapSessionsModalData({
            isOpen: true,
            overlapSessions,
        });
        trackClickEvent({
            subCategory: 'VIEW_OVERLAP_SESSIONS',
        });
    };

    const closeOverlapSessionsModal = () => {
        setOverlapSessionsModalData({
            isOpen: false,
            overlapSessions: null,
        });
        trackClickEvent({
            subCategory: 'CLOSE_OVERLAP_SESSIONS',
        });
    };

    const classes = useStyles();

    return (
        <>
            <div className={classes.dateContainer}>
                <DaySelect
                    className={classes.transparentDaySelect}
                    data={pageData}
                    onChange={onDayPickerChange}
                    selected={pagination}
                />
            </div>
            {showCheckAll ? (
                <div className={classes.checkAllButtonContainer}>
                    <CheckAllButton checked={checkAllChecked} onClick={onCheckAllClicked} />
                </div>
            ) : null}
            <div className={classes.timeButtons} ref={buttonWrapperRef}>
                {timesToDisplay?.map(({ time, text, overlapSessions }) => (
                    <TimePickerButtonWrapper
                        key={time.toISOString()}
                        tooltipBoundaryRef={buttonWrapperRef}
                        tooltipText={
                            times.some(t => t.time)
                                ? 'Deselect'
                                : 'Your private calendar is visible only to you'
                        }
                    >
                        <TimeButton
                            variant={variant}
                            text={text}
                            overlapActivities={overlapSessions}
                            className={classes.timeButton}
                            containerClassName={classes.timeButtonContainer}
                            startTime={time}
                            endTime={
                                duration === undefined
                                    ? undefined
                                    : addTime(time, { minutes: duration / (1000 * 60) })
                            }
                            timeZone={timeZone}
                            selected={selectedDates.some(selectedTime => +selectedTime === +time)}
                            onClickHandle={() => onTimeSlotClick(time, text)}
                            aria-label={time.toISOString()}
                            openOverlapSessionsModal={openOverlapSessionsModal}
                        />
                    </TimePickerButtonWrapper>
                )) || null}
                {timesToDisplay?.length < thisPageTimes?.times.length ? (
                    <MainButton
                        before={<AddFill className={classes.foldIcon} />}
                        className={classes.foldButton}
                        onClick={() => onLoadMoreClick()}
                    >
                        Load more times
                    </MainButton>
                ) : null}
            </div>

            <OverlapSessionsModal
                overlapSessionsData={overlapSessionsModalData}
                timeZone={timeZone}
                closeModal={closeOverlapSessionsModal}
            />
        </>
    );
};

const useStyles = makeStyles<DefaultTheme>(
    theme => ({
        dateContainer: {
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& > div': {
                maxWidth: '100%',
            },
        },
        transparentDaySelect: {
            '& *': {
                background: 'transparent',
            },
        },
        timeButtons: {
            display: 'flex',
            flexWrap: 'wrap',
            overflowY: 'auto',
        },
        timeButtonContainer: {
            marginBottom: 8,
            width: '100%',
        },
        timeButton: {
            width: '100%',
        },
        checkAllButtonContainer: {
            marginBottom: 16,
        },
        foldButton: {
            ...theme.typography.t81,
            paddingTop: 0,
            border: 0,
            '&:hover': {
                border: 0,
                background: 'transparent',
            },
            color: '#40A4FF',
        },
        foldIcon: {
            height: '10px',
            width: '10px',
            marginRight: 2,
        },
    }),
    { classNamePrefix: 'time-picker' }
);

export default TimePicker;
